import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { gameCategories, articles, faqItems, siteInfo } from '@/mocks/mahjongData';

export default function Home() {
  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20elegant%20traditional%20Chinese%20mahjong%20scene%20with%20dark%20wooden%20table%20and%20scattered%20ivory%20tiles%20featuring%20Chinese%20characters%2C%20soft%20warm%20ambient%20lighting%2C%20red%20and%20gold%20subtle%20accents%2C%20artistic%20composition%2C%20cinematic%20depth%20of%20field%2C%20refined%20aesthetic%2C%20professional%20photography&width=1800&height=900&seq=mahjong-hero-bg&orientation=landscape"
              alt="麻将电脑版在线玩 - 免费经典麻将立即开局"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight animate-fade-up opacity-0">
              麻将电脑版在线玩
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl mx-auto mb-2 leading-relaxed animate-fade-up animation-delay-200 opacity-0">
              免费经典麻将，打开浏览器立即开局
            </p>
            <p className="text-sm md:text-lg text-white/70 max-w-xl mx-auto mb-8 animate-fade-up animation-delay-300 opacity-0">
              无需下载安装 · 支持单机对战与多人联机 · 随时随地享受麻将乐趣
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up animation-delay-400 opacity-0">
              <Link
                to="/category/jingdian-mahjong"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                立即免费开局
              </Link>
              <Link
                to="/category/single-player"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-base font-medium hover:bg-background-50/30 transition-all whitespace-nowrap cursor-pointer"
              >
                单机练习模式
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-primary-500 py-8">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-2xl md:text-3xl font-heading text-background-50">{siteInfo.users}</p>
                <p className="text-sm text-primary-100 mt-1">注册玩家</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading text-background-50">{siteInfo.games}</p>
                <p className="text-sm text-primary-100 mt-1">累计开局</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading text-background-50">{siteInfo.categories}+</p>
                <p className="text-sm text-primary-100 mt-1">玩法种类</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-heading text-background-50">24/7</p>
                <p className="text-sm text-primary-100 mt-1">全天候服务</p>
              </div>
            </div>
          </div>
        </section>

        {/* Game Categories */}
        <section id="game-categories" className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                多种麻将玩法任你选
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                从经典到地方特色，从竞技到休闲，总有一款适合你
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {gameCategories.map((game) => (
                <Link
                  key={game.slug}
                  to={`/category/${game.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {game.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{game.shortDesc}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {game.features.map((f) => (
                        <span
                          key={f}
                          className="px-2 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                为什么选择麻将电脑版在线玩？
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                六大优势，打造最优质的网页麻将在线游戏体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-4">
                  <i className="ri-computer-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">无需下载安装</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  麻将在网页浏览器中直接运行，无需下载任何软件或插件。打开浏览器即可立即开始游戏，省去繁琐的安装步骤。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-money-cny-circle-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">完全免费</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  免费麻将游戏不下载不收费，核心游戏功能永久免费开放。无需担心隐藏费用或强制消费。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-lg mb-4">
                  <i className="ri-global-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">多人在线联机</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  麻将多人联机电脑版支持全球玩家实时对战，可与好友创建私人房间，也可匹配陌生人切磋技艺。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-4">
                  <i className="ri-smartphone-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">跨平台兼容</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  不论Windows还是Mac系统，只要有浏览器就能畅玩。界面自适应不同屏幕尺寸，兼容各种设备。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">安全可靠</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  网页麻将在线游戏无需下载，从根源上杜绝病毒和恶意软件风险。数据加密保护，游戏环境安全可信。
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-100 text-secondary-700 rounded-lg mb-4">
                  <i className="ri-customer-service-2-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">贴心客服支持</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  7×24小时客服在线，随时解答游戏问题。内置新手指引和规则说明，让每位玩家都能快速上手。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                  麻将攻略与资讯
                </h2>
                <p className="text-foreground-600 text-sm">提升牌技，了解麻将文化</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2 py-1 text-xs bg-accent-100 text-accent-700 rounded-full mb-2">
                      {article.category}
                    </span>
                    <h3 className="font-heading text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/article/mahjong-beginner-guide"
                className="inline-flex items-center gap-2 px-6 py-2 border border-primary-300 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多文章
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="w-full px-4 md:px-6 max-w-[1024px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600">关于麻将电脑版在线玩的常见疑问解答</p>
            </div>

            <div className="space-y-4">
              {faqItems.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer select-none">
                    <span className="font-heading text-foreground-900 pr-4">{faq.question}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20Chinese%20tea%20room%20with%20mahjong%20table%20in%20center%2C%20soft%20golden%20hour%20light%20streaming%20through%20window%2C%20traditional%20Chinese%20decor%2C%20inviting%20and%20relaxed%20atmosphere%2C%20professional%20interior%20photography&width=1800&height=600&seq=mahjong-cta-bg&orientation=landscape"
              alt="立即开始免费麻将游戏"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl text-white mb-4">
              准备好体验最畅快的在线麻将了吗？
            </h2>
            <p className="text-white/80 max-w-lg mx-auto mb-8">
              免费麻将游戏不下载，打开浏览器立即开局。{siteInfo.users} 位玩家的共同选择。
            </p>
            <Link
              to="/category/jingdian-mahjong"
              className="inline-block px-10 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              立即免费开局
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteInfo.name,
            url: 'https://mahjong-online.com/',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://mahjong-online.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${siteInfo.name} - 免费经典麻将立即开局`,
            description: siteInfo.description,
            url: 'https://mahjong-online.com/',
            isPartOf: {
              '@type': 'WebSite',
              name: siteInfo.name,
              url: 'https://mahjong-online.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}